'use client';

import { motion } from 'framer-motion';
import * as React from 'react';

const formatCharacter = (char: string) => (char === ' ' ? '\u00A0' : char);

const CHAR_STYLE: React.CSSProperties = {
  position: 'absolute',
  display: 'inline-block',
  backfaceVisibility: 'hidden',
};

type RollingTextProps = Omit<React.ComponentProps<'span'>, 'children'> & {
  text: string;
  duration?: number;
  staggerDelay?: number;
  loopDelay?: number;
};

function RollingText({
  text,
  duration = 0.5,
  staggerDelay = 0.05,
  loopDelay = 1,
  ...props
}: RollingTextProps) {
  const parts = React.useMemo(() => text.split(/(\s+)/), [text]);
  const totalChars = React.useMemo(() => text.replace(/\s/g, '').length, [text]);
  const totalAnimationTime = totalChars * staggerDelay + duration + 0.3 + loopDelay;

  let charIdx = 0;

  return (
    <span data-slot="rolling-text" {...props}>
      {parts.map((part, wi) => {
        if (/^\s+$/.test(part)) {
          return <span key={`space-${wi}`}>{part}</span>;
        }

        const chars = Array.from(part);
        return (
          <span key={`word-${wi}`} style={{ display: 'inline-block', whiteSpace: 'nowrap' }}>
            {chars.map((char, ci) => {
              const thisIdx = charIdx++;
              const charDelay = thisIdx * staggerDelay;
              return (
                <span
                  key={`c-${wi}-${ci}`}
                  style={{
                    position: 'relative',
                    display: 'inline-block',
                    perspective: '9999999px',
                    transformStyle: 'preserve-3d',
                    width: 'auto',
                  }}
                  aria-hidden="true"
                >
                  <motion.span
                    style={{
                      ...CHAR_STYLE,
                      transformOrigin: '50% 25%',
                    }}
                    animate={{ rotateX: [0, 0, 90, 90] }}
                    transition={{
                      duration: totalAnimationTime,
                      times: [
                        0,
                        charDelay / totalAnimationTime,
                        (charDelay + duration) / totalAnimationTime,
                        1,
                      ],
                      ease: 'easeOut',
                      repeat: Infinity,
                    }}
                  >
                    {formatCharacter(char)}
                  </motion.span>
                  <motion.span
                    style={{
                      ...CHAR_STYLE,
                      transformOrigin: '50% 100%',
                    }}
                    animate={{ rotateX: [90, 90, 0, 0] }}
                    transition={{
                      duration: totalAnimationTime,
                      times: [
                        0,
                        (charDelay + 0.3) / totalAnimationTime,
                        (charDelay + 0.3 + duration) / totalAnimationTime,
                        1,
                      ],
                      ease: 'easeOut',
                      repeat: Infinity,
                    }}
                  >
                    {formatCharacter(char)}
                  </motion.span>
                  <span style={{ visibility: 'hidden' }}>{formatCharacter(char)}</span>
                </span>
              );
            })}
          </span>
        );
      })}

      <span className="sr-only">{text}</span>
    </span>
  );
}

export { RollingText, type RollingTextProps };
